import MapboxDraw from '@mapbox/mapbox-gl-draw';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { Feature, GeoJsonProperties, Geometry } from 'geojson';

interface InitialState {
  drawTool: MapboxDraw | undefined;
  isDrawing: boolean;
  mode: DrawMode;
  polygonClicks: number;
  isHoveringOverVertex: boolean;
  isTooltipVisible: boolean;
  seeMeasurements: boolean;
  currentLineHoverFeature?: Feature<Geometry, GeoJsonProperties>;
  useBoundaryType?: boolean;
}

type DrawMode = 'polygon' | 'rectangle' | 'circle' | 'edit' | 'delete' | null;

const initialState: InitialState = {
  currentLineHoverFeature: undefined,
  drawTool: undefined,
  isDrawing: false,
  isHoveringOverVertex: false,
  isTooltipVisible: true,
  mode: null,
  polygonClicks: 0,
  seeMeasurements: false,
  useBoundaryType: false,
};

const drawV2Slice = createSlice({
  initialState,
  name: 'draw-v2',
  reducers: {
    clearDrawMode: (state) => {
      state.drawTool = initialState.drawTool;
      state.isDrawing = initialState.isDrawing;
      state.mode = initialState.mode;
      state.polygonClicks = initialState.polygonClicks;
      state.isHoveringOverVertex = initialState.isHoveringOverVertex;
      state.seeMeasurements = initialState.seeMeasurements;
      state.useBoundaryType = initialState.useBoundaryType;
    },
    incrementPolygonClick: (state) => {
      state.polygonClicks += 1;
    },
    setCurrentLineHoverFeature: (
      state,
      action: PayloadAction<Feature<Geometry, GeoJsonProperties>>
    ) => {
      state.currentLineHoverFeature = action.payload;
    },
    setDrawTool: (state, action: PayloadAction<MapboxDraw | undefined>) => {
      state.drawTool = action.payload;
    },
    setIsDrawing: (state, action: PayloadAction<boolean>) => {
      state.isDrawing = action.payload;
    },
    setIsHoveringOverVertex: (state, action: PayloadAction<boolean>) => {
      state.isHoveringOverVertex = action.payload;
    },
    setMode: (state, action: PayloadAction<DrawMode>) => {
      state.polygonClicks = initialState.polygonClicks;
      state.mode = action.payload;
    },
    setSeeMeasurements: (state, action: PayloadAction<boolean>) => {
      state.seeMeasurements = action.payload;
    },
    setTooltipVisibility: (state, action: PayloadAction<boolean>) => {
      state.isTooltipVisible = action.payload;
    },
    setUseBoundaryType: (state, action: PayloadAction<boolean>) => {
      state.useBoundaryType = action.payload;
    },
  },
});

export const drawV2Selector = (state) => state?.drawV2;

export const isHoveringOverVertexSelector = (state) =>
  drawV2Selector(state)?.isHoveringOverVertex;

export const {
  setDrawTool,
  clearDrawMode,
  setIsDrawing,
  setMode,
  incrementPolygonClick,
  setIsHoveringOverVertex,
  setTooltipVisibility,
  setSeeMeasurements,
  setCurrentLineHoverFeature,
  setUseBoundaryType,
} = drawV2Slice.actions;

export default drawV2Slice.reducer;
